import { getInviteHashLocalStorage } from '../utils/localStorage'
import { isLicensedPhysician } from './qualificationsChecks'

const FMA = 'Suomen Lääkäriliitto'
const studentMemberKind = 'Opiskelija, ei jäsen'
const targetOrganizations = [FMA] // other could be added here when they have agreed to use this service for applications

const isCurrentMember = (user: any, filter?: (membership: any) => boolean) => {
  if (!user || !Array.isArray(user.memberships)) {
    return false
  }
  const currentDate = new Date().toISOString()
  const currentMembership = user.memberships.find(
    (m: any) =>
      targetOrganizations.includes(m.yhdistys_name) &&
      m.els_jasenalkupvm &&
      m.els_jasenalkupvm < currentDate &&
      (!m.els_jasenloppu || m.els_jasenloppu > currentDate) &&
      (!filter || filter(m))
  )
  return currentMembership != undefined
}

// to show or hide student application button
export const isCurrentAnyMemberOfFMA = (user: any) =>
  isCurrentMember(user, membership => membership.yhdistys_name === FMA)

// to show or hide member application button
export const isCurrentProperMemberOfFMA = (user: any) =>
  isCurrentMember(
    user,
    membership =>
      membership.yhdistys_name === FMA &&
      !membership.els_jasentyyppi3_els_jasentyyppi.endsWith(studentMemberKind)
  )

// to show or hide menu links
export const isCurrentAnyMemberOfFMAOrOtherOrganization = (user: any) =>
  isCurrentMember(user, membership => targetOrganizations.includes(membership.yhdistys_name))

// check if there is an invite hash in local storage
export const isStudentInvite = (): boolean => {
  return getInviteHashLocalStorage() !== null
}

// check student membership status from sidosryhmat
// If user has active membership, membership takes precedence over student affiliation
export const isStudent = (user: any): boolean => {
  if (!user || !user.sidosryhmat) {
    return false
  }

  // If user has active membership, membership takes precedence over student affiliation
  if (isCurrentAnyMemberOfFMAOrOtherOrganization(user)) {
    return false
  }

  const studentMembership = user.sidosryhmat.find((sidosryhma: any) => {
    // if els_paataso is "Lääketieteen opiskelijat"
    const isStudentGroup = sidosryhma['paataso.els_paataso'] === 'Lääketieteen opiskelijat'
    // if there is no end date or it is in the future
    const hasNoEndDateOrInFuture =
      !sidosryhma['els_paattymispaiva'] || new Date(sidosryhma['els_paattymispaiva']) > new Date()
    return isStudentGroup && hasNoEndDateOrInFuture
  })

  return !!studentMembership
}

export const userFoundInAapeli = (user: any) => {
  return isStudent(user) || isCurrentAnyMemberOfFMA(user)
}

/** Jäsenyys voi alkaa 1.7. vain 1.1.–31.5. välisenä aikana (ei menneisyyteen). */
export const isJulyMembershipStartAvailable = (): boolean => {
  const month = new Date().getMonth()
  return month < 5
}

export const showStudentMembershipApplication = (user: any): boolean => {
  if (!user || !user.sidosryhmat || !isStudent(user)) {
    return false
  }

  // Calculate date 3 years and 4 months (40 months) ago
  const now = new Date()
  const minStartDate = new Date(now)
  minStartDate.setMonth(minStartDate.getMonth() - 40)

  // Check if student status started early enough
  const hasValidStartDate = user.sidosryhmat.find((sidosryhma: any) => {
    const isStudentGroup = sidosryhma['paataso.els_paataso'] === 'Lääketieteen opiskelijat'
    const startedEarlyEnough = new Date(sidosryhma['els_alkupaiva']) <= minStartDate
    const notEnded =
      !sidosryhma['els_paattymispaiva'] || new Date(sidosryhma['els_paattymispaiva']) > now
    return isStudentGroup && startedEarlyEnough && notEnded
  })

  // Check that user has no current or past FMA membership
  const hasNoFMAMembership = !user.memberships?.some((m: any) => m.yhdistys_name === FMA)

  return hasValidStartDate && hasNoFMAMembership
}
// Check if user has permission to use dev user switcher
// export const hasDevUserSwitcherPermission = (user: any): boolean => {
//     if (!user || !user.sidosryhmat) {
//         return false
//     }
//
//     // Look for specific permission in sidosryhmat
//     const hasPermission = user.sidosryhmaHierarkia.some((sidosryhmaHierarkia: any) => {
//         const hasCorrectPaataso = sidosryhmaHierarkia['paataso.els_paataso'] === 'Tietoni palvelu'
//         const hasCorrectTarkenne =
//             sidosryhmaHierarkia['tarkenne.els_sidosryhmantarkenne'] === 'Switch user permission'
//
//         // Check if permission is currently active (no end date or end date in future)
//         const isActive =
//             !sidosryhmaHierarkia['els_paattymispaiva'] ||
//             new Date(sidosryhmaHierarkia['els_paattymispaiva']) > new Date()
//         console.log()
//
//         return (hasCorrectPaataso || hasCorrectTarkenne) && isActive
//     })
// }
// Check if user has permission to use dev user switcher
export const hasDevUserSwitcherPermission = (user: any): boolean => {
  if (!user) {
    console.log('hasDevUserSwitcherPermission: No user provided')
    return false
  }

  if (!user.sidosryhmaHierarkia) {
    console.log('hasDevUserSwitcherPermission: No sidosryhmaHierarkia data found in user:', user)
    return false
  }

  // Look for specific permission in sidosryhmaHierarkia
  const hasPermission = user.sidosryhmaHierarkia.some((sidosryhmaHierarkia: any) => {
    console.log('Checking sidosryhmaHierarkia item:', sidosryhmaHierarkia)

    const hasCorrectPaataso = sidosryhmaHierarkia['paataso.els_paataso'] === 'Tietoni palvelu'
    const hasCorrectTarkenne =
      sidosryhmaHierarkia['tarkenne.els_sidosryhmantarkenne'] === 'Switch user permission'

    // Check if permission is currently active (no end date or end date in future)
    const isActive =
      !sidosryhmaHierarkia['els_paattymispaiva'] ||
      new Date(sidosryhmaHierarkia['els_paattymispaiva']) > new Date()

    console.log('Permission check:', {
      hasCorrectPaataso,
      hasCorrectTarkenne,
      isActive,
      paataso: sidosryhmaHierarkia['paataso.els_paataso'],
      tarkenne: sidosryhmaHierarkia['tarkenne.els_sidosryhmantarkenne'],
      paattymispaiva: sidosryhmaHierarkia['els_paattymispaiva']
    })

    return hasCorrectPaataso && hasCorrectTarkenne && isActive
  })

  console.log('hasDevUserSwitcherPermission result:', hasPermission)
  return hasPermission
}
const ASSOCIATION_NAME = 'Suomen Lääkäriliitto'
const MEMBERSHIP_TYPE_FIELD = 'els_jasentyyppi3_els_jasentyyppi'
const getCurrentMembershipType = (user: any): string | undefined => {
  if (!Array.isArray(user?.memberships)) {
    return undefined
  }
  const now = new Date().toISOString()
  const currentMembership = user.memberships.find((membership: any) => {
    const startsBeforeNow = membership.els_jasenalkupvm && membership.els_jasenalkupvm < now
    const hasNotEnded = !membership.els_jasenloppu || membership.els_jasenloppu > now
    return membership.yhdistys_name === ASSOCIATION_NAME && startsBeforeNow && hasNotEnded
  })
  return currentMembership?.[MEMBERSHIP_TYPE_FIELD]
}
const hasMembershipType = (user: any, acceptedTypes: string[]): boolean => {
  const membershipType = getCurrentMembershipType(user)
  return acceptedTypes.some(type => membershipType?.includes(type))
}
export const isStudentMember = (user: any): boolean => hasMembershipType(user, ['Opiskelijajäsen'])
export const isPhysicianMember = (user: any): boolean => hasMembershipType(user, ['Lääkärijäsen'])
export const isRetiredMember = (user: any): boolean => hasMembershipType(user, ['Eläkeläisjäsen'])
export const isHonorMember = (user: any): boolean => hasMembershipType(user, ['Kunniajäsen'])
